// src/components/dependants/richNoteViewer.component.jsx
import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import "quill/dist/quill.snow.css";
import { useBoxContext } from "../../../../Contexts/boxData.context.jsx";

/**
 * Props:
 * - html: string (the saved Quill HTML)
 * - showToolbar?: boolean (default true) — show a disabled toolbar for visual parity
 */
export default function RichNoteViewer({ html, noteData = {}, closeNoSave = () => { } }) {

  const {
    setNoteBoxID, setNoteBoxOpen
  } = useBoxContext();

  const [showCopy, setShowCopy] = React.useState(false);

  const safe = useMemo(
    () => DOMPurify.sanitize(html || "", { USE_PROFILES: { html: true } }),
    [html]
  );

  const editorRef = React.useRef(null);
  const timerRef = React.useRef(null);

  const LetsClose = () => {
    return () => {
      setNoteBoxID(null);
      setNoteBoxOpen(false);
    };
  }

  const letsCopy = () => {
    return () => {
      navigator.clipboard.writeText(html || "");
    };
  };

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Rich copy: put both HTML and plain text on the clipboard (fallback to text)
  const handleCopy = async () => {
    try {
      const el = editorRef.current;
      const htmlToCopy = el ? el.innerHTML : (safe || "");
      const textToCopy = el ? el.innerText : (html || "");

      if (navigator.clipboard && window.ClipboardItem) {
        const item = new ClipboardItem({
          "text/html": new Blob([htmlToCopy], { type: "text/html" }),
          "text/plain": new Blob([textToCopy], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
      } else {
        // Fallback: at least copy plain text so users don't see raw tags
        await navigator.clipboard.writeText(textToCopy);
      }

      setShowCopy(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setShowCopy(false), 1000);
    } catch (err) {
      // optional: handle fallback or show error
      console.error("Copy failed", err);
    }
  };

  return (
    <div className="rich-note-viewer">
      <div className="alert-light d-flex align-items-center mb-2 fs-7 rounded gap-2">
        <div className="d-flex gap-2 align-items-start flex-wrap">
          <div className="col-13">
            <div className="text-muted small lh-1 mb-1">Note Title</div>
            <div className="d-flex gap-1 align-items-center">
              <input
                type="text"
                className="form-control fs-7"
                value={noteData.SourceData || ""}
                placeholder="Add a title…"
                readOnly
              />
            </div>
          </div>

          <div className="col-10">
            <div className="text-muted small lh-1 mb-1">Date of Contact</div>
            <div className="d-flex gap-1 align-items-center">
              <input
                type="text"
                className="form-control fs-7"
                value={noteData.DateOfContact}
                placeholder="YYYY-MM-DD"
                readOnly
              />
            </div>
          </div>

          <div className="col-8">
            <div className="text-muted small lh-1 mb-1">By</div>
            <div className="d-flex gap-1 align-items-center">
              <input
                type="text"
                className="form-control fs-7"
                readOnly
                value={noteData?.ByName || ""}
              />
            </div>
          </div>

          <div className="col-13">
            <div className="text-muted small lh-1 mb-1">Synch Date</div>
            <div className="d-flex gap-1 align-items-center">
              <input
                type="text"
                className="form-control fs-7"
                readOnly
                value={noteData?.synchdate || ""}
              />
            </div>
          </div>
        </div>


        <div className="ms-auto col-12 justify-content-end d-flex gap-2">
          <button
            className={
              showCopy
                ? "btn btn-sm btn-success w-50"
                : "btn btn-sm btn-outline-info w-50"
            }
            type="button"
            onClick={handleCopy}
          >
            {showCopy ? "Note copied.." : "Copy"}
          </button>
          {/* <button
            className="btn btn-sm btn-outline-secondary w-50"
            type="button"
            onClick={LetsClose()}
          >
            Close
          </button> */}
        </div>
      </div>
      {/* Render as Quill-looking container/editor but not editable */}
      <div className="ql-container ql-snow ql-disabled" aria-disabled="true">
        <div
          className="ql-editor"
          ref={editorRef}
          // Do NOT make this contentEditable; it should be display-only
          contentEditable={false}
          dangerouslySetInnerHTML={{ __html: safe }}
        />
      </div>

      <style>{`
        /* Match your editor's height behavior */
        .rich-note-viewer .ql-container {
          min-height: calc(100vh - 180px);
          overflow-y: auto;
        }

        /* Visually indicate disabled toolbar; prevent interactions */
        .rich-note-viewer .ql-toolbar.ql-disabled,
        .rich-note-viewer .ql-toolbar.ql-disabled * {
          pointer-events: none !important;
          opacity: 0.55;
        }
        .rich-note-viewer .ql-container.ql-disabled .ql-editor {
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
